/**
 * What colour an aim is.
 *
 * An aim is a preview of an effect, so it has to be made of the same stuff the
 * effect will be made of. The board used to pick from three flat swatches — red for
 * harm, green for a boon, cyan otherwise — which says what the skill DOES and
 * nothing at all about what it IS: a fireball, an ice lance and a thrown rock all
 * aimed in the same red, and then landed in three different materials. The player
 * learns nothing from the aim that the icon had not already told them.
 *
 * So the palette is read off the same two axes every impact is: the violation gives
 * the material (Heat's orange, Cold's pale blue, Darkness' violet) and the intent
 * pulls it toward what is being done with it. Exactly the composition
 * `createDecalMaterial` runs in `intentTint`, done once in JS so the 2D field and
 * the WebGL preview cannot disagree about the colour of the same shot.
 *
 * Three stops, not one, for the reason `VIOLATION_PROFILES` carries three: the tint
 * is a ramp from a near-white centre out through the material to where it cools,
 * and a single mid-tone laid on a near-black board reads as a sticker.
 */

import { violationProfile } from './violation';
import {
  INTENTS, intentFor, pathOf, skillViolation,
} from './presentation';

/**
 * Refused: out of range, or aimed at ground the shot cannot reach.
 *
 * Deliberately NOT red and deliberately not an alarm. Red is a material this game
 * uses for heat and for blood, so a red refusal collides with half the skill list;
 * and an aim a player holds while they hunt for a legal cell must not shout at them
 * the whole time. What it is instead is the light going OUT of the material — a
 * cold slate with no emitting centre — so a denied aim reads as unlit rather than as
 * an error.
 */
export const AIM_DENIED_PALETTE = {
  core: { r: 206, g: 218, b: 236 },
  color: { r: 158, g: 172, b: 194 },
  deep: { r: 28, g: 34, b: 46 },
  denied: true,
};

function rgb(hex) {
  const value = parseInt(String(hex).replace('#', ''), 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

function blend(a, b, amount) {
  return {
    r: Math.round(a.r + (b.r - a.r) * amount),
    g: Math.round(a.g + (b.g - a.g) * amount),
    b: Math.round(a.b + (b.b - a.b) * amount),
  };
}

/**
 * The intent reaches the three stops by different amounts on purpose.
 *
 * A centre pulled all the way to the intent's colour loses the material — every mend
 * would have the same green heart whatever it is made of — while a deep end left
 * untouched leaves the cool edge arguing with the warm middle. So the middle stop,
 * which is most of what is on screen, carries the intent hardest; the emitting
 * centre keeps its material; and the cool end follows part of the way.
 */
const INTENT_REACH = { core: 0.28, color: 0.62, deep: 0.45 };

export function paletteForViolation(violation, intentName = 'harm', path = null) {
  const profile = violationProfile(violation, path);
  const intent = INTENTS[intentName] || INTENTS.harm;
  const stops = {
    core: rgb(profile.core),
    color: rgb(profile.color),
    deep: rgb(profile.deep),
  };
  if (!intent.bias || !intent.biasAmount) return { ...stops, denied: false };
  const bias = rgb(intent.bias);
  return {
    core: blend(stops.core, bias, intent.biasAmount * INTENT_REACH.core),
    color: blend(stops.color, bias, intent.biasAmount * INTENT_REACH.color),
    deep: blend(stops.deep, bias, intent.biasAmount * INTENT_REACH.deep),
    denied: false,
  };
}

/**
 * The palette an armed ability aims in.
 *
 * `valid: false` is not the ability's palette darkened — a dimmed fireball is still
 * a fireball, and the player has to be able to tell "this will land here, weakly"
 * from "this will not land". A refusal is its own material.
 */
export function aimPalette(ability, { valid = true, resolvedImpact = null } = {}) {
  if (!valid) return AIM_DENIED_PALETTE;
  return paletteForViolation(
    skillViolation(ability, resolvedImpact),
    intentFor(ability, resolvedImpact),
    pathOf(ability),
  );
}
